import React, { useCallback, useState } from 'react';
import { Card, TabLayout } from 'src/@vymo/ui/blocks';
import { ReactComponent as DetailsIcon } from 'src/assets/icons/details.svg';
import { ReactComponent as EngagementIcon } from 'src/assets/icons/engagement.svg';
import { ReactComponent as HighlightsIcon } from 'src/assets/icons/highlights.svg';
import { ReactComponent as PerformanceIcon } from 'src/assets/icons/performance.svg';
import { ReactComponent as RelatedRecordsIcon } from 'src/assets/icons/relatedRecords.svg';
import { ReactComponent as HistoryIcon } from 'src/assets/icons/reset.svg';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import { SPG_WIDGET } from 'src/modules/constants';
import { useAppDispatch } from 'src/store/hooks';
import ChildComponent from 'src/workspace/childComponent';
import { singleCardRequest } from '../queries';
import { fetchHeaderDetails, fetchUserTab } from '../thunk';
import { User360Config } from '../types';
import User360Card from '../user360Card';
import ProfileDetails from '../user360Card/details';
import styles from '../index.module.scss';

function UserTabs({
  tabConfig,
  userCode,
}: {
  tabConfig: User360Config;
  userCode: string;
}) {
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useState(
    tabConfig.tabs?.[0]?.code || '',
  );
  const isSPGEnabled = getGrowthBookFeatureFlag('enable-spg');

  const onRefresh = useCallback(
    (cardConfig, tab) => {
      const cardRequest: any = singleCardRequest(cardConfig, tab, userCode);
      if (cardRequest) {
        dispatch(fetchUserTab(cardRequest));
      }
    },
    [dispatch, userCode],
  );

  const getCardChildren = (type, card, tab) => {
    switch (type) {
      case 'profile':
        return (
          <ProfileDetails
            card={card}
            userCode={userCode}
            onRefresh={() => dispatch(fetchHeaderDetails({ userCode }))}
          />
        );
      case 'widget_card':
        return isSPGEnabled ? (
          <ChildComponent
            id={SPG_WIDGET}
            props={{
              locationCode: 'user_details',
              itemCode: userCode,
              widgetCode: card.code,
            }}
          />
        ) : null;
      default:
        return (
          <User360Card
            card={card}
            titleAction={(tabCode) => setSelectedTab(tabCode)}
            onRefresh={() => onRefresh(card, tab)}
          />
        );
    }
  };

  const getTabHeaderIcon = (tabCode: string) => {
    switch (tabCode) {
      case 'highlights':
        return <HighlightsIcon />;

      case 'details':
        return <DetailsIcon />;

      case 'engagement':
        return <EngagementIcon />;

      case 'related_records':
      case 'appointments':
      case 'licenses':
        return <RelatedRecordsIcon />;

      case 'history':
        return <HistoryIcon />;
      case 'performance':
        return <PerformanceIcon />;

      default:
        return null;
    }
  };

  return (
    <Card classNames={styles.tabWrapper}>
      <TabLayout
        classNames={styles.tabLayout}
        items={(tabConfig?.tabs || [])
          .filter((tab) => tab.code !== 'performance')
          .map((tab) => ({
            key: tab.code,
            label: tab.name,
            icon: getTabHeaderIcon(tab.code),
            children:
              tab?.cards?.length > 1 && tab.code !== 'highlights' ? (
                <TabLayout
                  items={tab.cards.map((card) => ({
                    key: card.code,
                    label: card.name,
                    children: getCardChildren(card.card_type, card, tab),
                  }))}
                  defaultKey={tab.cards?.[0]?.code || ''}
                />
              ) : (
                <div className={styles.tabLayoutChildren}>
                  <div className={styles.cardWrapper}>
                    {tab.cards.map((card) =>
                      getCardChildren(card.card_type, card, tab),
                    )}
                  </div>
                </div>
              ),
          }))}
        defaultKey={selectedTab}
        onChange={(key) => {
          setSelectedTab(key);
        }}
      />
    </Card>
  );
}

export default UserTabs;
